import React, { useState, useEffect, useRef, useCallback } from "react";
import { graphql } from "gatsby";
import { GatsbyImage as Img, getImage } from "gatsby-plugin-image";
import { Link as ScrollLink } from "react-scroll";
import CloseIcon from "../images/icons/material/close.svg";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import elencoComuni from "../data/gi_db_comuni/gi_comuni.json";
import TextInput from "react-autocomplete-input";

const FORM_ENDPOINT = process.env.GATSBY_FORM_AGRIVOLTAICO_ENDPOINT;

const mandatoryFields = [
  "firstName",
  "lastName",
  "phoneNumber",
  "privacyAgreement",
  "comune",
  "ettariEstensioneTerreno",
];

const ContactFormRifredi = ({ data: { image, privacyText } }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phoneNumber: "",
    emailAddress: "",
    privacyAgreement: false,
    comune: "",
    ettariEstensioneTerreno: "",
    origineTerreno: "",
    foglioMappa: "",
    particelleCatastali: "",
    qualitaTerreno: "",
    statoGiuridicoUso: "",
    coltivazioni: "",
  });

  const onChange = useCallback((e) => {
    const { name, value, checked, type } = e.target;
    setFormData((data) => ({
      ...data,
      [name]:
        type === "checkbox"
          ? name === "privacyAgreement"
            ? checked
            : getCheckedBoxes(name)
          : value,
    }));
  }, []);

  const getCheckedBoxes = (chkboxName) => {
    let checkboxes = document.getElementsByName(chkboxName);
    let checkboxesChecked = [];

    checkboxes.forEach((element) => {
      if (element.checked) {
        checkboxesChecked.push(element.value);
      }
    });

    return checkboxesChecked.length > 0 ? checkboxesChecked.toString() : "";
  };

  const [privacyVisible, setPrivacyVisible] = useState(false);
  const showPrivacy = useCallback(() => setPrivacyVisible(true), []);
  const closePrivacy = useCallback(() => setPrivacyVisible(false), []);

  const formRef = useRef(null);

  const { executeRecaptcha } = useGoogleReCaptcha();

  const [submitted, setSubmitted] = useState(false);
  const [validationError, setValidationError] = useState(null);
  const [remoteError, setRemoteError] = useState(null);
  const [errorsMap, setErrorsMap] = useState({});
  const [focusMap, setFocusMap] = useState({});
  const [showForm, setShowForm] = useState();
  const [loading, setLoading] = useState(false);

  const onFocus = useCallback((e) => {
    const { name } = e.target;
    setFocusMap((map) => ({ ...map, [name]: true }));
  }, []);

  const onBlur = useCallback((e) => {
    const { name } = e.target;
    setFocusMap((map) => ({ ...map, [name]: false }));
  }, []);

  useEffect(() => {
    const loadComuni = async () => {
      const listaComuni = [];
      Array.from(elencoComuni).forEach(function (_comune) {
        let optionKey =
          _comune.denominazione_ita.toUpperCase() +
          " (" +
          _comune.sigla_provincia +
          ")";
        listaComuni.push(optionKey);
      });
      setComuni(listaComuni);
    };
    loadComuni();
  }, []);

  const [comuni, setComuni] = useState([]);

  const getComuneError = useCallback(
    (comuneField) => {
      if (!comuneField) {
        return "Specificare il comune";
      } else if (!comuni.includes(comuneField)) {
        return "Ci spiace, il comune che hai inserito sembra non esistere o è una frazione";
      }
      return undefined;
    },
    [comuni]
  );

  const validateForm = useCallback(
    (formData) => {
      const errors = mandatoryFields.reduce((errors, field) => {
        if (field === "comune") {
          return { ...errors, [field]: getComuneError(formData[field]) };
        }
        return { ...errors, [field]: !formData[field] };
      }, {});
      setErrorsMap(errors);
      const formValid =
        Object.values(errors).find((val) => !!val) === undefined;
      if (!formValid) {
        setRemoteError(null);
      }
      return formValid;
    },
    [getComuneError]
  );

  useEffect(() => {
    if (validationError) {
      const formValid = validateForm(formData);
      if (formValid) {
        setValidationError(null);
      }
    }
  }, [validationError, formData, validateForm]);

  const groupClass = (name) =>
    `nj-form-group nj-form-group--icon-right${
      focusMap[name] ? " is-focused" : ""
    }${formData[name] ? " is-filled" : ""}${
      errorsMap[name] ? " has-warning" : ""
    }`;

  const [comuneValue, setComuneValue] = useState();

  const validateComune = useCallback(() => {
    setErrorsMap((data) => ({
      ...data,
      comune: comuneValue ? getComuneError(comuneValue) : false,
    }));
    setFormData((data) => ({
      ...data,
      comune: comuneValue,
    }));
  }, [comuneValue, getComuneError]);

  const submit = async (e) => {
    e.preventDefault();
    if (!validateForm(formData)) {
      console.warn("Form data not valid", formData);
      setValidationError("Completa i dati mancanti");
      return;
    }

    if (!executeRecaptcha) {
      console.warn("Execute recaptcha not yet available");
      return;
    }
    const token = await executeRecaptcha("formCorporate");

    setValidationError(null);
    setRemoteError(null);
    setLoading(true);

    try {
      const result = await submitForm({ ...formData, recaptcha: token });
      console.log("Submit result: ", result);
      setSubmitted(true);
      if (formRef.current) {
        formRef.current.scrollIntoView();
      }
    } catch (err) {
      console.error("Error submit: ", err);
      setSubmitted(false);
      setRemoteError("Si è verificato un errore");
    }
    setLoading(false);
  };

  const submitForm = async (values) => {
    const {
      firstName,
      lastName,
      phoneNumber,
      emailAddress,
      privacyAgreement,
      comune,
      ettariEstensioneTerreno,
      origineTerreno,
      foglioMappa,
      particelleCatastali,
      qualitaTerreno,
      statoGiuridicoUso,
      coltivazioni,
      recaptcha,
    } = values;

    const fetchUrl = FORM_ENDPOINT;
    const dataParams = new URLSearchParams();

    dataParams.append("firstName", firstName);
    dataParams.append("lastName", lastName);
    dataParams.append("phoneNumber", phoneNumber);
    dataParams.append("emailAddress", emailAddress);
    dataParams.append("privacyAgreement", privacyAgreement);
    dataParams.append(
      "qa",
      JSON.stringify({
        comune: comune,
        ettariEstensioneTerreno: ettariEstensioneTerreno,
        origineTerreno: origineTerreno,
        foglioMappa: foglioMappa,
        particelleCatastali: particelleCatastali,
        qualitaTerreno: qualitaTerreno,
        statoGiuridicoUso: statoGiuridicoUso,
        coltivazioni: coltivazioni,
      })
    );
    dataParams.append("recaptcha", recaptcha ? recaptcha : null);
    dataParams.append("originURL", window.location.href);
    dataParams.append("groupKey", "EngieCorporate");

    const res = await fetch(fetchUrl, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: dataParams,
    });
    if (!res.ok) {
      throw new Error(`Error: ${res.status} ${res.statusText}`);
    }
    const result = await res.json();
    if (result.result === "success") {
      return result;
    } else {
      throw new Error(`Form response not ok`);
    }
  };

  return (
    <div className="contact-form">
      {submitted === false && (
        <div className="container my-5">
          <div className="row">
            <div className="col-12">
              <div className="contact-form__first-question">
                Il titolo di proprietà del terreno deriva da donazione?
              </div>
              <div className="nj-form-group contact-form__first-answer">
                <div className="nj-radio">
                  <label className="contact-form__checkbox">
                    <input
                      type="radio"
                      name="donazione"
                      id="si"
                      value="si"
                      onClick={() => setShowForm(false)}
                    />
                    Si
                    <span className="nj-radio__decorator">
                      <span className="nj-radio__check"></span>
                    </span>
                  </label>
                </div>
                <div className="nj-radio">
                  <label className="contact-form__checkbox">
                    <input
                      type="radio"
                      name="donazione"
                      id="no"
                      value="no"
                      onClick={() => setShowForm(true)}
                    />
                    No
                    <span className="nj-radio__decorator">
                      <span className="nj-radio__check"></span>
                    </span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
      {showForm === false && (
        <div className="container my-5">
          <div className="row">
            <div className="col-12">
              <div className="contact-form__stop">
                Il terreno purtroppo non è idoneo, grazie per l'interesse.
              </div>
            </div>
          </div>
        </div>
      )}
      {showForm && (
        <div className="container my-5">
          <div className="row">
            <div className="col-lg-5 px-md-0">
              <div className="contact-form__wrapper p-3 p-md-6 d-flex flex-column h-100">
                {image && image.node && image.node.gatsbyImageData ? (
                  <Img
                    image={getImage(image.node)}
                    alt={image.node.description || ""}
                    className="contact-form__img"
                  />
                ) : null}
                {image && image.content ? (
                  <div className="contact-form__gradient-box position-relative text-white d-flex flex-column pl-4 pb-5 pr-4 pt-4">
                    {image.content.boxTitle && image.content.boxTitle.value ? (
                      <div className="contact-form__box-title contact-form__box-title--big mt-4">
                        {image.content.boxTitle.value}
                      </div>
                    ) : null}
                    {image.content.boxText && image.content.boxText.value ? (
                      <div
                        className="contact-form__box-subtitle mt-2"
                        dangerouslySetInnerHTML={{
                          __html: image.content.boxText.value,
                        }}
                      ></div>
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
            <div ref={formRef} className="col-lg-7 px-md-0">
              <div className="contact-form__content py-5 px-3 px-md-4 pr-md-4 h-100">
                {submitted ? (
                  <div className="row">
                    <div className="col-12">
                      <h3 className="contact-form__title">
                        Grazie per aver compilato il modulo
                      </h3>
                    </div>
                    <div className="col-12 mt-lg-5">
                      <div className="contact-form__question">
                        Ti ricontatteremo a breve per informarti dell'esito
                        della valutazione di idoneità del terreno.
                      </div>
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="row">
                      <div className="col-12">
                        <h3 className="contact-form__title">
                          Lascia i tuoi dati
                        </h3>
                      </div>
                    </div>
                    <form onSubmit={submit}>
                      <div className="row">
                        <div className="col-12">
                          <div className="contact-form__question">
                            Qual è l'origine del terreno?
                          </div>
                          <div className="nj-form-group">
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="origineTerreno"
                                  value="compravendita"
                                  onChange={onChange}
                                />
                                Compravendita
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="origineTerreno"
                                  value="eredità"
                                  onChange={onChange}
                                />
                                Eredità
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="origineTerreno"
                                  value="altro"
                                  onChange={onChange}
                                />
                                Altro
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="contact-form__border"></div>
                      <div className="row">
                        <div className="col-md-6">
                          <div className={groupClass("comune")}>
                            <div className="contact-form__text">
                              <TextInput
                                Component="input"
                                trigger=""
                                spacer=""
                                maxOptions={4}
                                className="nj-form-control"
                                onBlur={(e) => {
                                  onBlur(e);
                                  validateComune();
                                }}
                                onFocus={onFocus}
                                autoComplete="off"
                                id="contactFormComune"
                                name="comune"
                                options={comuni}
                                onChange={(value) => {
                                  setComuneValue(value);
                                }}
                              />
                              <label
                                className="nj-label-floating"
                                htmlFor="contactFormComune"
                              >
                                *Comune (provincia):
                              </label>
                            </div>
                          </div>
                          {errorsMap.comune ? (
                            <span className="contact-form__warning">
                              {errorsMap.comune}
                            </span>
                          ) : null}
                        </div>
                        <div className="col-md-6">
                          <div
                            className={groupClass("ettariEstensioneTerreno")}
                          >
                            <div className="contact-form__text">
                              <input
                                type="text"
                                className="nj-form-control"
                                name="ettariEstensioneTerreno"
                                id="contactFormEttariEstensioneTerreno"
                                onFocus={onFocus}
                                onBlur={onBlur}
                                onChange={onChange}
                                value={formData.ettariEstensioneTerreno}
                              />
                              <label
                                htmlFor="contactFormEttariEstensioneTerreno"
                                className="nj-label-floating"
                              >
                                *Estensione del terreno in ettari:
                              </label>
                            </div>
                          </div>
                          {errorsMap.ettariEstensioneTerreno ? (
                            <span className="contact-form__warning">
                              Specificare l'estensione del terreno
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="contact-form__border"></div>
                      <div className="row">
                        <div className="col-12">
                          <div className="contact-form__question">
                            Dati catastali
                          </div>
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6">
                          <div className={groupClass("foglioMappa")}>
                            <div className="contact-form__text">
                              <input
                                type="text"
                                className="nj-form-control"
                                name="foglioMappa"
                                id="contactFormFoglioMappa"
                                onFocus={onFocus}
                                onBlur={onBlur}
                                onChange={onChange}
                                value={formData.foglioMappa}
                              />
                              <label
                                htmlFor="contactFormFoglioMappa"
                                className="nj-label-floating"
                              >
                                Foglio di mappa
                              </label>
                            </div>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className={groupClass("particelleCatastali")}>
                            <div className="contact-form__text">
                              <input
                                type="text"
                                className="nj-form-control"
                                name="particelleCatastali"
                                id="contactFormParticelleCatastali"
                                onFocus={onFocus}
                                onBlur={onBlur}
                                onChange={onChange}
                                value={formData.particelleCatastali}
                              />
                              <label
                                htmlFor="contactFormParticelleCatastali"
                                className="nj-label-floating"
                              >
                                Particelle catastali
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="contact-form__border"></div>
                      <div className="row">
                        <div className="col-12">
                          <div className="contact-form__question">
                            Qualità del terreno
                          </div>
                          <div className="nj-form-group">
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="qualitaTerreno"
                                  value="pianeggiante"
                                  onChange={onChange}
                                />
                                Pianeggiante
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="qualitaTerreno"
                                  value="scosceso"
                                  onChange={onChange}
                                />
                                Scosceso
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="qualitaTerreno"
                                  value="parzialmente coltivato"
                                  onChange={onChange}
                                />
                                Parzialmente coltivato
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="qualitaTerreno"
                                  value="boscato"
                                  onChange={onChange}
                                />
                                Boscato
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="qualitaTerreno"
                                  value="alberi da frutto"
                                  onChange={onChange}
                                />
                                Alberi da frutto
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="qualitaTerreno"
                                  value="pietroso"
                                  onChange={onChange}
                                />
                                Pietroso
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="qualitaTerreno"
                                  value="pascolo"
                                  onChange={onChange}
                                />
                                Pascolo
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="qualitaTerreno"
                                  value="montagne"
                                  onChange={onChange}
                                />
                                Montagne
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="qualitaTerreno"
                                  value="avvallamenti o dirupi"
                                  onChange={onChange}
                                />
                                Avvallamenti o dirupi
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="qualitaTerreno"
                                  value="abbandonato"
                                  onChange={onChange}
                                />
                                Abbandonato
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="qualitaTerreno"
                                  value="discarica"
                                  onChange={onChange}
                                />
                                Discarica
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="qualitaTerreno"
                                  value="recintato"
                                  onChange={onChange}
                                />
                                Recintato
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="qualitaTerreno"
                                  value="facilmente accessibile"
                                  onChange={onChange}
                                />
                                Facilmente accessibile
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="qualitaTerreno"
                                  value="accessibile con servitù di passaggio"
                                  onChange={onChange}
                                />
                                Accessibile con servitù di passaggio
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="qualitaTerreno"
                                  value="non so/preferico non dichiarare ora"
                                  onChange={onChange}
                                />
                                Non so/Preferisco non dichiarare ora
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="contact-form__border"></div>
                      <div className="row">
                        <div className="col-12">
                          <div className="contact-form__question">
                            Attuale stato giuridico d'uso
                          </div>
                          <div className="nj-form-group">
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="statoGiuridicoUso"
                                  value="coltivato privatamente"
                                  onChange={onChange}
                                />
                                Coltivato privatamente
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="statoGiuridicoUso"
                                  value="coltivato con azienda agricola"
                                  onChange={onChange}
                                />
                                Coltivato con azienda agricola
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="statoGiuridicoUso"
                                  value="abbandonato"
                                  onChange={onChange}
                                />
                                Abbandonato
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="statoGiuridicoUso"
                                  value="subaffittato per agricoltura"
                                  onChange={onChange}
                                />
                                Subaffittato per agricoltura
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="statoGiuridicoUso"
                                  value="vincolato da ipoteca"
                                  onChange={onChange}
                                />
                                Vincolato da ipoteca
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="statoGiuridicoUso"
                                  value="non so/preferisco non dichiarare ora"
                                  onChange={onChange}
                                />
                                Non so/Preferisco non dichiarare ora
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="contact-form__border"></div>
                      <div className="row">
                        <div className="col-12">
                          <div className="contact-form__question">
                            Se coltivato, ci sono coltivazioni di pregio o
                            biologiche?
                          </div>
                          <div className="nj-form-group">
                            <div className="nj-radio">
                              <label className="contact-form__checkbox">
                                <input
                                  type="radio"
                                  id="si"
                                  name="coltivazioni"
                                  value="si"
                                  onChange={onChange}
                                />
                                Si
                                <span className="nj-radio__decorator">
                                  <span className="nj-radio__check"></span>
                                </span>
                              </label>
                            </div>
                            <div className="nj-radio">
                              <label className="contact-form__checkbox">
                                <input
                                  type="radio"
                                  id="no"
                                  name="coltivazioni"
                                  value="no"
                                  onChange={onChange}
                                />
                                No
                                <span className="nj-radio__decorator">
                                  <span className="nj-radio__check"></span>
                                </span>
                              </label>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div className="contact-form__border"></div>
                      <div className="row">
                        <div className="col-md-6">
                          <div className={groupClass("firstName")}>
                            <div className="contact-form__text">
                              <input
                                type="text"
                                className="nj-form-control"
                                name="firstName"
                                id="contactFormFirstName"
                                onFocus={onFocus}
                                onBlur={onBlur}
                                onChange={onChange}
                                value={formData.firstName}
                              />
                              <label
                                htmlFor="contactFormFirstName"
                                className="nj-label-floating"
                              >
                                *Nome
                              </label>
                            </div>
                          </div>
                          {errorsMap.firstName ? (
                            <span className="contact-form__warning">
                              Specificare il nome
                            </span>
                          ) : null}
                        </div>
                        <div className="col-md-6">
                          <div className={groupClass("lastName")}>
                            <div className="contact-form__text">
                              <input
                                type="text"
                                className="nj-form-control"
                                name="lastName"
                                id="contactFormLastName"
                                onFocus={onFocus}
                                onBlur={onBlur}
                                onChange={onChange}
                                value={formData.lastName}
                              />
                              <label
                                htmlFor="contactFormLastName"
                                className="nj-label-floating"
                              >
                                *Cognome
                              </label>
                            </div>
                          </div>
                          {errorsMap.lastName ? (
                            <span className="contact-form__warning">
                              Specificare il cognome
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-md-6">
                          <div className={groupClass("phoneNumber")}>
                            <div className="contact-form__text">
                              <input
                                type="text"
                                className="nj-form-control"
                                name="phoneNumber"
                                id="contactFormPhoneNumber"
                                onFocus={onFocus}
                                onBlur={onBlur}
                                onChange={onChange}
                                value={formData.phoneNumber}
                              />
                              <label
                                htmlFor="contactFormPhoneNumber"
                                className="nj-label-floating"
                              >
                                *Telefono
                              </label>
                            </div>
                          </div>
                          {errorsMap.phoneNumber ? (
                            <span className="contact-form__warning">
                              Specificare il telefono
                            </span>
                          ) : null}
                        </div>
                        <div className="col-md-6">
                          <div className={groupClass("emailAddress")}>
                            <div className="contact-form__text">
                              <input
                                type="emailAddress"
                                className="nj-form-control"
                                name="emailAddress"
                                id="contactFormEmailAddress"
                                onFocus={onFocus}
                                onBlur={onBlur}
                                onChange={onChange}
                                value={formData.emailAddress}
                              />
                              <label
                                htmlFor="contactFormEmailAddress"
                                className="nj-label-floating"
                              >
                                Indirizzo email
                              </label>
                            </div>
                          </div>
                          {errorsMap.emailAddress ? (
                            <span className="contact-form__warning">
                              Specificare email
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="contact-form__border"></div>
                      <div className="row">
                        <div className="col-12">
                          <div className="nj-form-group">
                            <div className="nj-checkbox">
                              <label className="contact-form__checkbox">
                                <input
                                  type="checkbox"
                                  name="privacyAgreement"
                                  value="true"
                                  onChange={onChange}
                                />
                                *Acconsento al trattamento dei dati personali
                                per le finalità
                                <span className="nj-checkbox__decorator">
                                  <span className="nj-checkbox__check"></span>
                                </span>
                              </label>
                              <span> </span>
                              <ScrollLink
                                to="contact-form-rifredi-privacy-text"
                                smooth={true}
                                onClick={showPrivacy}
                                className="contact-form__privacy-link"
                              >
                                indicate
                              </ScrollLink>
                            </div>
                          </div>
                          {errorsMap.privacyAgreement ? (
                            <span className="contact-form__warning">
                              Acconsentire al trattamento dei dati
                            </span>
                          ) : null}
                        </div>
                      </div>
                      <div className="row">
                        <div className="col-12">
                          <div className="nj-form-group">
                            <button
                              type="submit"
                              className="nj-btn nj-btn--primary"
                              disabled={loading}
                            >
                              {loading ? (
                                <>
                                  Loading...
                                  <div
                                    className="nj-spinner nj-spinner--xs ml-1"
                                    role="status"
                                  >
                                    <span className="sr-only">Loading...</span>
                                  </div>
                                </>
                              ) : (
                                <span>Invia</span>
                              )}
                            </button>
                            {validationError ? (
                              <span className="ml-3 contact-form__warning">
                                {validationError}
                              </span>
                            ) : null}
                            {remoteError ? (
                              <span className="ml-3 contact-form__warning">
                                {remoteError}
                              </span>
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>
          </div>
          <div className="row">
            <div className="col-12 px-md-0">
              <span
                id="contact-form-rifredi-privacy-text"
                className="scroll-anchor"
              ></span>
              <div
                className={`contact-form__privacy-text${
                  privacyVisible ? "" : " d-none"
                }`}
              >
                <button
                  className="contact-form__privacy-close"
                  onClick={closePrivacy}
                >
                  <CloseIcon className="material-svg" />
                </button>
                {privacyText && privacyText.value ? (
                  <div
                    className="p-3"
                    dangerouslySetInnerHTML={{ __html: privacyText.value }}
                  ></div>
                ) : null}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactFormRifredi;
export const contactFormFragment = graphql`
  fragment ContactFormRifrediFragment on LiferayFormContattiRifredi {
    liferayFields {
      siteId
      articleId
    }
    image {
      node {
        description
        gatsbyImageData(width: 670)
      }
      content {
        boxTitle {
          value
        }
        boxText {
          value
        }
      }
    }
    privacyText {
      value
    }
  }
`;
